import {
  BookOpen,
  ChartNoAxesCombined,
  Grid2X2,
  LayoutDashboard,
  Shapes,
  Sigma,
  Timer,
} from "./icons";
import { examPrepProductMap } from "../exam-prep/common/constants";

// Server error codes that mean "access gated" (upgrade / daily limit); a caught
// error carrying one of these routes to the upgrade flow instead of a toast.
export const ACCESS_GATE_CODES = Object.freeze([
  "UPGRADE_REQUIRED",
  "DAILY_LIMIT_REACHED",
]);

// Pricing (INR). Mirrors the server's DMAT_PREP_PRICING (exam-prep/dmat/common
// constants) — the two tiers can't share a module, so keep them in sync by hand.
export const pricing = Object.freeze({
  advanced: 423,
  advancedReference: 1999,
  mock: 99,
  mockReference: 299,
  bundle: 129,
  bundleReference: 598,
});

// Display fallbacks for the server overview counts before it has loaded.
export const FALLBACK_QUESTION_COUNT = "700+";
export const FALLBACK_PRACTICE_COUNT = 464;

// The worked example shown on the landing showcase before a Basic user has history.
export const practiceExample = Object.freeze({
  id: "guide:math:ME-MEDIUM-01",
  topic: "math",
  difficulty: "medium",
  stem: "What is the value of B?",
  equations: ["A + B = 9", "C / 2 + A = B", "C / 2 = 7"],
  options: ["8", "1", "14", "9"],
  correct: [0],
  explanation:
    "**The correct answer is 8.**\n\n1. Start with the equation containing one unknown: `C / 2 = 7`, so `C = 14`.\n2. Substitute into the second equation: `7 + A = B`. Combine this with `A + B = 9`: `A + (7 + A) = 9`. Therefore `2A = 2`, so `A = 1`.\n3. Now find the value asked for: `B = 7 + 1 = 8`.\n\n**Why 1 is incorrect:** it is the value of A, not B. Always check which letter the question asks you to find.\n\nCheck all three equations: `1 + 8 = 9`, `14 / 2 + 1 = 8`, and `14 / 2 = 7`.",
});

// Demo analytics for the landing tracker showcase, keyed by prep-window in days.
export const showcaseWeeks = Object.freeze({
  7: { answered: 64, accuracy: 78, lessons: 4, values: [6, 10, 8, 12, 10, 8, 10] },
  28: { answered: 218, accuracy: 74, lessons: 9, values: [32, 54, 68, 64] },
});

// Fixed demo scalars on the landing tracker showcase (streak tile + latest mock card).
export const showcaseDemo = Object.freeze({
  streak: "7 days",
  mockScore: 78,
  mockCorrect: 80,
  mockTotal: 103,
  mockTitle: "dMAT Mock Test 1",
});

// The dMAT mock paper structure (section, duration) shown on the mocks screen.
export const mockFormat = Object.freeze([
  ["Figure Sequences", "25 min"],
  ["Mathematical Equations", "25 min"],
  ["Latin Squares", "25 min"],
  ["Break", "30 min"],
  ["General Academic Module", "90 min"],
]);

// Maps a Checkout selection to its marketplace service slug.
export const serviceSlugBySelection = examPrepProductMap("dmat");

// Per-topic accent colours for the landing topic cards, keyed by topic colour.
// dmat topic tokens (extended Tailwind palette), applied via className. Exact
// original accent hexes: purple #ede9f8/#6a5c82, orange #f9eee3/#87684c,
// blue #e8eff7/#5a7085, green #eaf1dd/#637247.
export const topicColours = Object.freeze({
  purple: "bg-dmat-topic-purple-bg text-dmat-topic-purple-text",
  orange: "bg-dmat-topic-orange-bg text-dmat-topic-orange-text",
  blue: "bg-dmat-topic-blue-bg text-dmat-topic-blue-text",
  green: "bg-dmat-topic-green-bg text-dmat-topic-green-text",
});

// The static 5x5 latin-square letters for the decorative <GridArt> DOM fallback.
export const GRID_ART_LETTERS = Object.freeze([
  "B", "D", "A", "?", "C",
  "D", "", "B", "C", "",
  "C", "", "E", "", "",
  "A", "", "C", "B", "D",
  "E", "C", "", "A", "B",
]);

// Product-tour steps (Onboarding). `basic` toggles copy + a couple of targets
// (free-practice vs topics) for Basic vs Advanced users.
export const tourSteps = (basic) => [
  {
    route: "/app",
    target: "practice-start",
    title: "Start here each day",
    text: basic
      ? "Basic includes two questions a day. Start here, or pick up a saved session."
      : "Start a practice session here, or pick up where you left off.",
  },
  {
    route: "/app",
    target: "progress",
    title: "See what your practice adds up to",
    text: "Your answers, accuracy and streak update as you practice. There is nothing to log manually.",
  },
  {
    route: "/app/practice",
    target: basic ? "free-practice" : "topics",
    interactive: !basic,
    title: basic ? "Try your free questions" : "Choose a topic",
    text: basic
      ? "Try both questions. Check each answer to see its worked solution."
      : "Choose a topic to filter the question list.",
  },
  {
    route: "/app/learn",
    target: "lesson",
    title: "Learn the method first",
    text: basic
      ? "This lesson is included in Basic. Read it before practicing; the remaining lessons unlock with Advanced."
      : "Open a lesson when you need a method or worked example. Mark it complete after reading.",
  },
  {
    route: "/app/mocks",
    target: "mocks",
    title: basic
      ? "When you are ready for a timed test"
      : "Take a full-length mock",
    text: basic
      ? "Upgrade to Advanced for mock access, timed sections and a detailed answer review."
      : "Your included mock is ready. Check the test timings before you begin. Additional papers are available below.",
  },
  {
    route: "/app/analytics",
    target: "analytics",
    title: "Use your results to plan the next session",
    text: basic
      ? "Advanced adds topic analysis and mock reports. Your practice activity is still saved on Basic."
      : "Review your mock scores, topic accuracy and past answers here. Results appear after you complete a session.",
  },
  {
    route: "/app",
    target: "replay",
    title: "Come back whenever you need a hand",
    text: "Use the demo button to reopen this tour. You can now explore the questions at your own pace.",
  },
];

// Radio options for the mock-selection step in Checkout: the single chosen paper
// + the bundle. singleProduct/singleLabel vary by which paper the user opened;
// the bundle saving is derived (2 papers - bundle price), not hardcoded.
export const mockSelectionOptions = (singleProduct, singleLabel) => [
  [
    singleProduct,
    `${singleLabel} only`,
    "One additional paper",
    pricing.mock,
    pricing.mockReference,
  ],
  [
    "mock:bundle",
    "Mock 2 + Mock 3",
    `₹${pricing.mock * 2 - pricing.bundle} less than buying separately`,
    pricing.bundle,
    pricing.bundleReference,
  ],
];

// Sidebar workspace navigation: [route, label, icon].
export const navigation = Object.freeze([
  ["/app", "Dashboard", LayoutDashboard],
  ["/app/practice", "Practice questions", Shapes],
  ["/app/mocks", "Mock tests", Timer],
  ["/app/analytics", "Analytics & scores", ChartNoAxesCombined],
  ["/app/learn", "Learning library", BookOpen],
]);

// Bottom bar navigation on phones: [route, short label, icon].
export const mobileNavigation = Object.freeze([
  ["/app", "Home", LayoutDashboard],
  ["/app/practice", "Practice", Shapes],
  ["/app/mocks", "Mocks", Timer],
  ["/app/learn", "Learn", BookOpen],
  ["/app/analytics", "Scores", ChartNoAxesCombined],
]);

// Maps a topic id to its icon (pairs with topicColours above).
export const topicIcons = Object.freeze({
  figures: Shapes,
  math: Sigma,
  latin: Grid2X2,
  gam: BookOpen,
});

// Landing curriculum cards, one per dMAT task type: [icon, title, blurb, colour].
export const taskTypes = Object.freeze([
  [Shapes, "Figure Sequences", "Track movement, colour and orientation.", "purple"],
  [Sigma, "Mathematical Equations", "Find whole-number solutions to equations.", "orange"],
  [Grid2X2, "Latin Squares", "Find the missing letter using rows and columns.", "blue"],
  [BookOpen, "General Academic Module", "Read information. Apply it to new questions.", "green"],
]);

// Landing FAQ accordion: [question, answer].
export const landingFaqs = Object.freeze([
  [
    "Which question types can I practice?",
    "Figure Sequences, Mathematical Equations, Latin Squares and General Academic Module questions. Each topic has practice questions with worked explanations.",
  ],
  [
    "What do I get for free?",
    "Basic includes two practice questions per day, one introductory lesson and your activity tracker. Upgrade for full practice, all lessons, analytics and mock access.",
  ],
  [
    "Will my progress be saved?",
    "Yes. Your account stores your answers, completed lessons, saved questions and mock attempts. Sign back in to continue. A timed mock keeps running when you close or refresh the page.",
  ],
  [
    "What does Advanced include?",
    "Full practice, every lesson, topic analytics and a full-length mock test with score reports, answer review and retakes.",
  ],
  [
    "What does a mock score mean?",
    "It is your percentage of correct tasks in that practice paper. Figure Sequence tasks count as correct only when both matrices are correct. It is not an official scaled score, percentile or admission prediction.",
  ],
]);

// Basic-plan feature bullets on the pricing cards.
export const basicPlanFeatures = Object.freeze([
  "2 practice questions per day",
  "1 introductory lesson",
  "Daily goal and activity tracking",
]);

// Advanced-plan feature bullets; the question count is passed from server overview.
export const advancedPlanFeatures = (practiceCount) => [
  `${practiceCount} original practice questions`,
  "Mock test access with score report and retakes",
  "Scores, explanations and question review",
  "Full learning library and 14-day sprint",
  "Topic analytics and performance history",
];

// Practice-set size options offered in the builder, keyed by plan.
export const practiceSetSizes = Object.freeze({
  basic: [1, 2],
  advanced: [5, 10, 20, 40],
});

// Daily practice-goal choices (Advanced) on the account screen.
export const dailyGoalOptions = Object.freeze([2, 5, 10, 20, 30]);

// Demo "accuracy by topic" bars on the landing tracker showcase: [topic, accuracy %].
export const showcaseTopicAccuracy = Object.freeze([
  ["Mathematical Equations", 84],
  ["Latin Squares", 78],
  ["Figure Sequences", 72],
  ["General Academic Module", 68],
]);

// Weekday labels for the 7-day landing showcase chart.
export const weekdayLabels = Object.freeze([
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
]);
